import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const TIERS = ["free", "starter", "pro", "enterprise"];
const TIER_COLOR = { free: "#888", starter: "#1f7a1f", pro: "#b1810a", enterprise: "#7a3da3" };

// All HC admin endpoints accept the agency_owner JWT cookie set by /api/auth/login.
const cfg = { withCredentials: true };

export default function HCSettingsPage() {
  const [matrix, setMatrix] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [country, setCountry] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  async function load() {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        axios.get(`${API}/api/hc/admin/feature-matrix`, cfg),
        axios.get(`${API}/api/hc/admin/tenants`, {
          ...cfg,
          params: { country: country || undefined, tier: tierFilter || undefined },
        }),
      ]);
      setMatrix(m.data);
      setTenants(t.data.items || []);
    } catch (e) {
      setMsg("Failed to load: " + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [country, tierFilter]);

  function updateFeatureTier(key, newTier) {
    setMatrix(m => ({
      ...m,
      features: { ...m.features, [key]: { ...m.features[key], min_tier: newTier } },
    }));
  }
  function updateLockedCopy(key, copy) {
    setMatrix(m => ({
      ...m,
      features: { ...m.features, [key]: { ...m.features[key], locked_copy: copy } },
    }));
  }
  async function saveMatrix() {
    setSaving(true);
    try {
      await axios.post(`${API}/api/hc/admin/feature-matrix`,
        { features: matrix.features }, cfg);
      setMsg("Feature matrix saved.");
    } catch (e) {
      setMsg("Save failed: " + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  }

  async function setTenantTier(client_id, tier) {
    try {
      await axios.post(`${API}/api/hc/admin/tenants/${client_id}/tier`,
        { tier }, cfg);
      setTenants(ts => ts.map(t => t.client_id === client_id ? { ...t, tier } : t));
      setMsg(`${client_id} → ${tier}`);
    } catch (e) {
      setMsg("Tier update failed: " + (e?.response?.data?.detail || e.message));
    }
  }

  if (loading) return <div style={{padding:32}}>Loading…</div>;
  if (!matrix) return <div style={{padding:32,color:"#a64423"}}>Unable to load HC settings. Check admin token.</div>;

  return (
    <div data-testid="hc-settings-page" style={{padding:"32px 40px",maxWidth:1200,margin:"0 auto",fontFamily:"system-ui,sans-serif"}}>
      <h1 style={{fontFamily:"Georgia,serif",color:"#1a1408",marginBottom:6}}>HustleConnect — Agency Settings</h1>
      <p style={{color:"#666",marginTop:0}}>Tune what each tier unlocks, then assign tiers per tenant. Changes save immediately and propagate to WP plugin admin pages within 60 seconds (cached).</p>

      {msg && <div data-testid="hc-msg" style={{padding:"8px 14px",background:"#fff8e6",border:"1px solid #ecdeb7",borderRadius:8,margin:"12px 0",color:"#5a4a1a"}}>{msg}</div>}

      <h2 style={{marginTop:32,color:"#1a1408"}}>Feature matrix</h2>
      <div style={{background:"#fff",border:"1px solid #ecdeb7",borderRadius:10,overflow:"hidden"}}>
        <table data-testid="hc-feature-matrix-table" style={{width:"100%",borderCollapse:"collapse",fontSize:14}}>
          <thead style={{background:"#faf3e0"}}>
            <tr>
              <th style={th}>Feature</th>
              <th style={th}>Category</th>
              <th style={th}>Min tier</th>
              <th style={th}>Locked copy (shown to clients)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(matrix.features).map(([key, f]) => (
              <tr key={key} style={{borderTop:"1px solid #f0e9d8"}}>
                <td style={td}><strong>{f.label}</strong><br/><small style={{color:"#999"}}>{key}</small></td>
                <td style={td}><span style={catChip}>{f.category}</span></td>
                <td style={td}>
                  <select
                    data-testid={`tier-select-${key}`}
                    value={f.min_tier}
                    onChange={e => updateFeatureTier(key, e.target.value)}
                    style={sel}
                  >
                    {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </td>
                <td style={td}>
                  <input
                    data-testid={`locked-copy-${key}`}
                    type="text"
                    value={f.locked_copy || ""}
                    onChange={e => updateLockedCopy(key, e.target.value)}
                    placeholder="e.g. Upgrade to Pro to unlock…"
                    style={{width:"100%",padding:6,border:"1px solid #ddd0a8",borderRadius:6}}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{marginTop:14}}>
        <button data-testid="save-matrix-btn" onClick={saveMatrix} disabled={saving}
                style={btnPrimary}>
          {saving ? "Saving…" : "Save feature matrix"}
        </button>
      </p>

      <h2 style={{marginTop:48,color:"#1a1408"}}>Tenants</h2>
      <div style={{display:"flex",gap:12,alignItems:"center",marginBottom:12}}>
        <label>Country:&nbsp;
          <select data-testid="filter-country" value={country} onChange={e => setCountry(e.target.value)} style={sel}>
            <option value="">All</option>
            {["AU","US","NZ","CA","UK"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>Tier:&nbsp;
          <select data-testid="filter-tier" value={tierFilter} onChange={e => setTierFilter(e.target.value)} style={sel}>
            <option value="">All</option>
            {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <span style={{color:"#999",fontSize:13}}>{tenants.length} tenant{tenants.length===1?"":"s"}</span>
      </div>
      <div style={{background:"#fff",border:"1px solid #ecdeb7",borderRadius:10,overflow:"hidden"}}>
        <table data-testid="hc-tenants-table" style={{width:"100%",borderCollapse:"collapse",fontSize:14}}>
          <thead style={{background:"#faf3e0"}}>
            <tr>
              <th style={th}>Client ID</th>
              <th style={th}>Country</th>
              <th style={th}>Modules</th>
              <th style={th}>Tier</th>
              <th style={th}>Joined</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map(t => (
              <tr key={t.client_id} style={{borderTop:"1px solid #f0e9d8"}}>
                <td style={td}><code>{t.client_id}</code></td>
                <td style={td}>{t.country}</td>
                <td style={td}>
                  {(t.modules || []).map(m => (
                    <span key={m} style={modChip}>{m}</span>
                  ))}
                </td>
                <td style={td}>
                  <select
                    data-testid={`tenant-tier-${t.client_id}`}
                    value={t.tier}
                    onChange={e => setTenantTier(t.client_id, e.target.value)}
                    style={{...sel, background: TIER_COLOR[t.tier]+"22", color: TIER_COLOR[t.tier], fontWeight: 600}}
                  >
                    {TIERS.map(tt => <option key={tt} value={tt}>{tt}</option>)}
                  </select>
                </td>
                <td style={td}><small style={{color:"#999"}}>{(t.created_at||"").slice(0,10)}</small></td>
              </tr>
            ))}
            {tenants.length === 0 && (
              <tr><td colSpan={5} style={{...td, textAlign:"center", color:"#999", padding:24}}>No tenants yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th = {textAlign:"left",padding:"10px 14px",fontWeight:600,color:"#1a1408"};
const td = {padding:"10px 14px",verticalAlign:"top"};
const sel = {padding:"6px 10px",border:"1px solid #ddd0a8",borderRadius:6,background:"#fff"};
const btnPrimary = {padding:"10px 20px",background:"#b1810a",color:"#fff",border:"none",borderRadius:8,fontWeight:600,cursor:"pointer"};
const catChip = {fontSize:11,textTransform:"uppercase",letterSpacing:.5,background:"#f0e9d8",padding:"2px 8px",borderRadius:6,color:"#7a6b3a"};
const modChip = {display:"inline-block",fontSize:11,background:"#fff8e6",border:"1px solid #ecdeb7",padding:"2px 8px",borderRadius:6,color:"#7a6b3a",marginRight:4};
